import { AnimatePresence } from 'framer-motion';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import { VideoCompilerCompilationStatus } from '@/utils/enums';

import CompilationsTable from './components/CompilationsTable';
import NewCompilationModal from './components/NewCompilationModal';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 32px;

  ${PageHeading} {
    margin: 0;
  }
`;

const StyledControls = styled(Controls)`
  display: inline-flex;
  margin-bottom: 16px;
`;

const tabs = [
  {
    label: 'In Process',
    slug: 'in-process',
    status: VideoCompilerCompilationStatus.IN_PROCESS,
  },
  {
    label: 'Done',
    slug: 'done',
    status: VideoCompilerCompilationStatus.COMPLETED,
  },
  {
    label: 'Failed',
    slug: 'failed',
    status: VideoCompilerCompilationStatus.FAILED,
  },
  {
    label: 'Draft',
    slug: 'draft',
    status: VideoCompilerCompilationStatus.DRAFT,
  },
] as const;

export type ActiveTab = (typeof tabs)[number];

interface Props {
  status: VideoCompilerCompilationStatus;
}

const VideoCompilations: FC<Props> = ({ status }) => {
  const [showCreationModal, setShowCreationModal] = useState(false);

  const navigate = useNavigate();

  const activeTab = useMemo(() => {
    return tabs.find((tab) => tab.status === status)!;
  }, [status]);

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <Header>
        <PageHeading>Video Compiler</PageHeading>
        <Button onClick={() => setShowCreationModal(true)}>Create new</Button>
      </Header>
      <StyledControls>
        {tabs.map((tab, i) => (
          <ControlsButton
            key={i}
            onClick={() => navigate(`../${tab.slug}`, { relative: 'path' })}
            active={status === tab.status}
          >
            {tab.label}
          </ControlsButton>
        ))}
      </StyledControls>
      <CompilationsTable
        activeTab={activeTab}
        onCreateNew={() => setShowCreationModal(true)}
      />
      <AnimatePresence>
        {showCreationModal && (
          <NewCompilationModal onClose={() => setShowCreationModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default VideoCompilations;
